import { useMemo } from "react";

import { cn } from "@/lib/utils";
import { ClipboardCopy, MoveUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";

import { ReferenceRelationship } from "@/redux/features/candidateProfileSlice";

import { AdminReference } from "@/queries/admin/references";
import { useResendReferenceRequestEmail } from "@/mutations/admin/resend-reference-request-email";

const RELATIONSHIP_TO_PROMPT_MAP: Record<
  ReferenceRelationship | "unknown",
  string[]
> = {
  Manager: [
    "How long have you known them?",
    "In what capacity have you worked together?",
    "Would you rehire them? Why or Why not?",
    "Were there any areas where they particularly excelled?",
  ],
  Peer: [
    "How long have you known them?",
    "In what capacity have you worked together?",
    "Would you want to work with them again?",
    "Were there any areas where they particularly excelled?",
  ],
  Coworker: [
    "How long have you known them?",
    "In what capacity have you worked together?",
    "Would you want to work with them again?",
    "Were there any areas where they particularly excelled?",
  ],
  "Direct Report": [
    "How do you know them, and in what capacity have you worked together?",
    "Can you describe their key responsibilities?",
    "Would you work for this manager again?",
    "Were there any areas where they particularly excelled?",
  ],
  Investor: [
    "How do you know them, and in what capacity have you worked together?",
    "Can you describe their key responsibilities?",
    "Would you want them to support your business again?",
    "In your opinion, what are their strongest professional skills or attributes?",
  ],
  "Board Member": [
    "How do you know them, and in what capacity have you worked together?",
    "Can you describe their key responsibilities?",
    "Would you want them to support your business again?",
    "In your opinion, what are their strongest professional skills or attributes?",
  ],
  Client: [
    "How do you know them, and in what capacity have you worked together?",
    "Would you want them to support your business again?",
    "In your opinion, what are their strongest professional skills or attributes?",
    "Did they meet or exceed your expectations?",
  ],
  unknown: ["Unknown", "Unknown", "Unknown", "Unknown"],
} as const;

const ReferenceDrawer = ({
  reference,
  setOpen,
}: {
  reference?: AdminReference;
  setOpen: (v: boolean) => void;
}) => {
  if (!reference) {
    return;
  }

  return (
    <Sheet open={Boolean(reference)} onOpenChange={setOpen}>
      <SheetContent className="w-full max-w-full sm:max-w-md md:max-w-lg bg-background line-clamp">
        <SheetTitle className="sr-only">Reference Details</SheetTitle>
        <SheetDescription className="sr-only">
          Details on a reference
        </SheetDescription>
        <ScrollArea className="h-[calc(100vh-4.75rem)]" type="scroll">
          <div className="flex flex-col gap-8">
            <Heading reference={reference} />
            <div className="flex flex-col items-start gap-6 self-stretch">
              <DetailOverview reference={reference} />
              <ReferenceAnswers reference={reference} />
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

const Heading = ({ reference }: { reference: AdminReference }) => {
  const { resendReferenceRequestEmail, isResendingReferenceRequestEmail } =
    useResendReferenceRequestEmail(reference.id);

  const allowRequest = useMemo(() => {
    return reference.status === "Requested";
  }, [reference.status]);

  const { textColor, backgroundColor } = useMemo(() => {
    let backgroundColor: string, textColor: string;
    switch (reference.status) {
      case "Requested":
        backgroundColor = "bg-red-100";
        textColor = "text-red-600";
        break;
      case "Approved":
        backgroundColor = "bg-orange-100";
        textColor = "text-orange-600";
        break;
      case "Rejected":
        backgroundColor = "bg-blue-100";
        textColor = "text-blue-600";
        break;
    }

    return { textColor, backgroundColor };
  }, [reference.status]);

  return (
    <div className="flex flex-col items-start gap-4">
      <div className="flex flex-col items-start gap-6 self-stretch">
        {Boolean(reference.user.profilePictureLink) && (
          <div className="h-24 w-24 rounded-full overflow-hidden">
            <img
              className="h-full w-full object-cover"
              src={reference.user.profilePictureLink}
            />
          </div>
        )}
        <div className="flex flex-col items-start gap-0.5 self-stretch">
          <div className="flex flex-row items-center gap-4">
            <p className="text-xl text-[#181d27] font-semibold leading-[30px]">
              {reference.user.name}
            </p>
            <div
              className={cn(
                "py-1 px-2.5 flex flex-row items-center gap-2 rounded-full",
                backgroundColor,
              )}
            >
              <p
                className={cn(
                  "text-xs font-semibold tracking-[-0.24px]",
                  textColor,
                )}
              >
                {reference.status}
              </p>
            </div>
          </div>
        </div>
      </div>
      {allowRequest && (
        <Button
          variant="secondary"
          disabled={isResendingReferenceRequestEmail}
          onClick={() => resendReferenceRequestEmail()}
        >
          Resend Request
        </Button>
      )}
    </div>
  );
};

const DetailOverview = ({ reference }: { reference: AdminReference }) => {
  return (
    <>
      <div className="flex flex-col items-start gap-2 self-stretch">
        <p className="text-sm text-[#535862] font-medium">Reference</p>
        <p className="text-base text-[#414651] font-medium">
          {reference.referenceName ?? "Unknown"}
        </p>
      </div>
      <div className="flex flex-col items-start gap-2 self-stretch">
        <p className="text-sm text-[#535862] font-medium">Title</p>
        <p className="text-base text-[#414651] font-medium">
          {reference.title ?? "Unknown"}
        </p>
      </div>
      <div className="flex flex-col items-start gap-2 self-stretch">
        <p className="text-sm text-[#535862] font-medium">Company</p>
        <p className="text-base text-[#414651] font-medium">
          {reference.company ?? "Unknown"}
        </p>
      </div>
      <div className="flex flex-col items-start gap-2 self-stretch">
        <p className="text-sm text-[#535862] font-medium">Relationship</p>
        <p className="text-base text-[#414651] font-medium">
          {reference.relationship ?? "Unknown"}
        </p>
      </div>
      {Boolean(reference.email) && (
        <div
          className="flex flex-col items-start gap-2 self-stretch"
          onClick={() => {
            void navigator.clipboard.writeText(reference.email ?? "");
            toast.success("Email copied to clipboard");
          }}
        >
          <p className="text-sm text-[#535862] font-medium">Email</p>
          <div className="text-md font-semibold text-accent hover:underline hover:cursor-pointer hover:text-accent underline-offset-2 ml-2">
            <span className="flex flex-row items-center gap-0.5">
              Copy
              <ClipboardCopy size={16} />
            </span>
          </div>
        </div>
      )}
      {Boolean(reference.surveyLink) && (
        <div className="flex flex-col items-start gap-2 self-stretch">
          <p className="text-sm text-[#535862] font-medium">Survey Link</p>
          <a
            href={reference.surveyLink}
            target="_blank"
            rel="noreferrer"
            className="text-md font-semibold text-accent hover:underline hover:cursor-pointer hover:text-accent underline-offset-2 ml-2"
          >
            <span className="flex flex-row items-center gap-0.5">
              Link
              <MoveUpRight size={16} />
            </span>
          </a>
        </div>
      )}
    </>
  );
};

const ReferenceAnswers = ({ reference }: { reference: AdminReference }) => {
  if (!reference.responses.some((v) => Boolean(v))) {
    return;
  }

  return (
    <>
      <div className="h-[1px] w-full bg-[#E9EAEB]" />
      <div className="flex flex-col items-start gap-2 self-stretch">
        <p className="text-base text-[#181D27] font-medium">Responses</p>
        {RELATIONSHIP_TO_PROMPT_MAP[reference.relationship ?? "unknown"].map(
          (question, idx) => (
            <ResponseTile
              key={`reference-question-${idx}`}
              question={question}
              answer={reference.responses[idx]}
            />
          ),
        )}
      </div>
    </>
  );
};

const ResponseTile = ({
  question,
  answer,
}: {
  question?: string;
  answer?: string;
}) => {
  if (!question || !answer) {
    return;
  }

  return (
    <div className="flex flex-col p-3 gap-3 flex-1 items-start self-stretch rounded-xl bg-[#ED5C380D]">
      <p className="text-base font-semibold text-[#22234A] whitespace-break-space">
        {question}
      </p>
      <div className="p-3 flex-1 self-stretch rounded-xl bg-white">
        <p className="text-sm font-medium text-[#4E4F6C]">
          &quot;{answer}&quot;
        </p>
      </div>
    </div>
  );
};

export default ReferenceDrawer;
